import { bankRounding } from 'utils/rounding'
import { DECIMAL_PLACES } from 'constants/calcConstants'

interface CommissionItem {
  id: number
  value: number
}

interface RepItem {
  id: number
  name: string
}

// totalFeeAmount: dollar amount of the fee being split (e.g. commissionToRep or psfFee)
// value field means dollar amount (not percentage)
// assignAllToMainRep: when true, default split assigns full amount to main rep (index 0) instead of 50/50 house split
export const getInitialValuesForCommission = (
  selectedItems: RepItem[],
  existingCommission: CommissionItem[] | undefined,
  totalFeeAmount: number,
  assignAllToMainRep = false
) => {
  if (!selectedItems.length) return {}

  const submittedCommissionIds = existingCommission?.map(el => el?.id) || []
  const houseAmount = bankRounding(totalFeeAmount * 0.5, DECIMAL_PLACES)

  // Check if existing split amounts match the current fee total.
  // When the fee changes (e.g. PSF added during edit), stale amounts must be recalculated.
  const existingSum = existingCommission?.reduce((sum, el) => sum + (el?.value || 0), 0) ?? 0
  const existingSplitsMatchFee =
    existingCommission?.length && totalFeeAmount > 0 && Math.abs(existingSum - totalFeeAmount) < 0.01

  const initialValues = selectedItems.reduce<any>((acc, value, index, array) => {
    const accKey = `${value?.id}`
    const newIndex = existingCommission?.findIndex(el => el.id === value?.id)

    const hasExistingValue =
      existingSplitsMatchFee &&
      (newIndex === 0 || newIndex) &&
      (existingCommission?.[newIndex]?.value || existingCommission?.[newIndex]?.value === 0)

    const defaultSplitValue = assignAllToMainRep
      ? index === 0
        ? totalFeeAmount
        : 0
      : value?.id === 1
      ? houseAmount
      : bankRounding((totalFeeAmount - houseAmount) / (array.length - 1), DECIMAL_PLACES)

    const splitCommissionValue =
      submittedCommissionIds?.length && !submittedCommissionIds.includes(value?.id)
        ? 0
        : array.length === 1
        ? totalFeeAmount
        : hasExistingValue
        ? existingCommission?.[newIndex]?.value
        : defaultSplitValue

    return { ...acc, [accKey]: splitCommissionValue }
  }, {})

  const restCommission =
    totalFeeAmount - Object.values(initialValues).reduce((acc: number, el) => acc + Number(el), 0)

  if (restCommission > 0 && selectedItems.length > 1) {
    initialValues[selectedItems[1]?.id] =
      Number(initialValues[selectedItems[1]?.id]) + restCommission
  }

  return initialValues
}
